import { useState } from "react";
import {
  Building2, Plus, Trash2, RefreshCw, CheckCircle2,
  AlertCircle, X, ChevronDown, ChevronUp,
} from "lucide-react";
import {
  useGetMyInstitutionContactsQuery,
  useSubmitInstitutionContactMutation,
  useDeleteInstitutionContactMutation,
  type InstitutionContact,
  type InstitutionContactPayload,
} from "../api/chapterApi";

// ── helpers ───────────────────────────────────────────────────────────────────

function safeMsg(e: any): string {
  return (
    e?.data?.message ||
    e?.data?.errors
      ? Object.values(e?.data?.errors ?? {}).flat().join(" ")
      : e?.error?.data?.message ||
        e?.message ||
        "Something went wrong."
  );
}

function fmtDate(iso?: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric", month: "short", year: "numeric",
  });
}

function AccredBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    accredited:   "bg-green-100 text-green-700 border-green-200",
    unaccredited: "bg-red-100 text-red-700 border-red-200",
    unknown:      "bg-gray-100 text-gray-500 border-gray-200",
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border ${map[status] ?? map.unknown}`}>
      {status}
    </span>
  );
}

function ProgBadge({ type, other }: { type: string; other?: string }) {
  const label = type === "other" ? (other || "Other") : type.toUpperCase();
  return (
    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-indigo-50 text-indigo-700 border border-indigo-100">
      {label}
    </span>
  );
}

// ── delete confirmation ───────────────────────────────────────────────────────

function DeleteModal({
  entry, onConfirm, onClose, loading,
}: { entry: InstitutionContact; onConfirm: () => void; onClose: () => void; loading: boolean }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-8 space-y-5">
        <div className="flex items-start justify-between gap-4">
          <h3 className="font-bold text-gray-900">Delete Entry</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100">
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>
        <p className="text-sm text-gray-600 leading-relaxed">
          Are you sure you want to delete the entry for{" "}
          <span className="font-semibold text-gray-900">{entry.institution_name}</span>?
          This action cannot be undone.
        </p>
        <div className="flex gap-3">
          <button onClick={onClose} disabled={loading}
            className="flex-1 py-2.5 border rounded-lg text-sm font-semibold text-gray-600 hover:bg-gray-50 disabled:opacity-40">
            Cancel
          </button>
          <button onClick={onConfirm} disabled={loading}
            className="flex-1 py-2.5 bg-red-600 rounded-lg text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-40 flex items-center justify-center gap-2">
            {loading ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

// ── submission form ───────────────────────────────────────────────────────────

const EMPTY: InstitutionContactPayload = {
  institution_name: "",
  programme_type: "bsc",
  programme_other: "",
  hod_name: "",
  hod_email: "",
  hod_phone: "",
  accreditation_status: "unknown",
  contact_person_name: "",
  contact_person_email: "",
  contact_person_phone: "",
  additional_info: "",
};

function SubmitForm({ onSuccess }: { onSuccess: () => void }) {
  const [form, setForm]         = useState<InstitutionContactPayload>(EMPTY);
  const [showOptional, setShowOptional] = useState(false);
  const [submitContact, { isLoading }]  = useSubmitInstitutionContactMutation();
  const [error, setError]       = useState<string | null>(null);

  function set<K extends keyof InstitutionContactPayload>(k: K, v: InstitutionContactPayload[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await submitContact(form).unwrap();
      setForm(EMPTY);
      onSuccess();
    } catch (err: any) {
      setError(safeMsg(err));
    }
  }

  const inputCls = "w-full border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400";
  const labelCls = "block text-xs font-semibold text-gray-600 mb-1";

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {error && (
        <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          {error}
        </div>
      )}

      {/* Institution */}
      <div>
        <label className={labelCls}>Institution Name <span className="text-red-500">*</span></label>
        <input
          required
          value={form.institution_name}
          onChange={(e) => set("institution_name", e.target.value)}
          placeholder="e.g. University of Lagos"
          className={inputCls}
        />
      </div>

      {/* Programme type */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Programme Type <span className="text-red-500">*</span></label>
          <select
            required
            value={form.programme_type}
            onChange={(e) => set("programme_type", e.target.value as any)}
            className={inputCls}
          >
            <option value="bsc">B.Sc Quantity Surveying</option>
            <option value="hnd">HND Quantity Surveying</option>
            <option value="other">Other</option>
          </select>
        </div>
        {form.programme_type === "other" && (
          <div>
            <label className={labelCls}>Specify Programme</label>
            <input
              value={form.programme_other}
              onChange={(e) => set("programme_other", e.target.value)}
              placeholder="Specify programme name"
              className={inputCls}
            />
          </div>
        )}

        <div>
          <label className={labelCls}>Accreditation Status <span className="text-red-500">*</span></label>
          <select
            required
            value={form.accreditation_status}
            onChange={(e) => set("accreditation_status", e.target.value as any)}
            className={inputCls}
          >
            <option value="unknown">Unknown / Not Sure</option>
            <option value="accredited">Accredited</option>
            <option value="unaccredited">Unaccredited</option>
          </select>
        </div>
      </div>

      {/* Head of Department */}
      <div className="border-t pt-4">
        <div className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-3">Head of Department</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className={labelCls}>Name <span className="text-red-500">*</span></label>
            <input
              required
              value={form.hod_name}
              onChange={(e) => set("hod_name", e.target.value)}
              placeholder="Full name"
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>Email</label>
            <input
              type="email"
              value={form.hod_email}
              onChange={(e) => set("hod_email", e.target.value)}
              placeholder="hod@university.edu"
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>Phone</label>
            <input
              value={form.hod_phone}
              onChange={(e) => set("hod_phone", e.target.value)}
              placeholder="080x xxx xxxx"
              className={inputCls}
            />
          </div>
        </div>
      </div>

      {/* Optional: contact person + notes */}
      <div className="border-t pt-2">
        <button
          type="button"
          onClick={() => setShowOptional((v) => !v)}
          className="flex items-center gap-1.5 text-xs font-semibold text-indigo-600 hover:text-indigo-800"
        >
          {showOptional ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          {showOptional ? "Hide" : "Add"} Contact Person & Notes (optional)
        </button>

        {showOptional && (
          <div className="mt-4 space-y-4">
            <div className="text-xs font-bold uppercase tracking-widest text-gray-400">Department Contact Person</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className={labelCls}>Name</label>
                <input
                  value={form.contact_person_name}
                  onChange={(e) => set("contact_person_name", e.target.value)}
                  placeholder="Contact person name"
                  className={inputCls}
                />
              </div>
              <div>
                <label className={labelCls}>Email</label>
                <input
                  type="email"
                  value={form.contact_person_email}
                  onChange={(e) => set("contact_person_email", e.target.value)}
                  placeholder="contact@university.edu"
                  className={inputCls}
                />
              </div>
              <div>
                <label className={labelCls}>Phone</label>
                <input
                  value={form.contact_person_phone}
                  onChange={(e) => set("contact_person_phone", e.target.value)}
                  placeholder="080x xxx xxxx"
                  className={inputCls}
                />
              </div>
            </div>

            <div>
              <label className={labelCls}>Additional Information</label>
              <textarea
                rows={3}
                value={form.additional_info}
                onChange={(e) => set("additional_info", e.target.value)}
                placeholder="Any other details relevant to the institute or programme…"
                className={`${inputCls} resize-none`}
              />
            </div>
          </div>
        )}
      </div>

      <div className="pt-2">
        <button
          type="submit"
          disabled={isLoading}
          className="inline-flex items-center gap-2 px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold rounded-lg disabled:opacity-40 transition-colors"
        >
          {isLoading
            ? <RefreshCw className="w-4 h-4 animate-spin" />
            : <Plus className="w-4 h-4" />}
          Submit Institution
        </button>
      </div>
    </form>
  );
}

// ── main page ─────────────────────────────────────────────────────────────────

export default function InstitutionContactsPage() {
  const { data: entries = [], isLoading, isFetching, refetch, error: fetchError } =
    useGetMyInstitutionContactsQuery();
  const [deleteContact, { isLoading: deleting }] = useDeleteInstitutionContactMutation();

  const [deleteTarget, setDeleteTarget] = useState<InstitutionContact | null>(null);
  const [banner, setBanner]             = useState<{ ok: boolean; msg: string } | null>(null);
  const [showForm, setShowForm]         = useState(false);

  function flash(ok: boolean, msg: string) {
    setBanner({ ok, msg });
    setTimeout(() => setBanner(null), 4000);
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    try {
      await deleteContact(deleteTarget.id).unwrap();
      flash(true, "Entry deleted.");
      setDeleteTarget(null);
    } catch (e: any) {
      flash(false, safeMsg(e));
    }
  }

  const isForbidden = (fetchError as any)?.status === 403;

  return (
    <div className="space-y-6 max-w-5xl mx-auto p-6">

      {/* ── Toast banner ───────────────────────────────────────────────────── */}
      {banner && (
        <div className={`fixed top-6 right-6 z-50 flex items-center gap-3 px-5 py-3 rounded-xl shadow-xl text-sm font-bold text-white ${banner.ok ? "bg-green-600" : "bg-red-600"}`}>
          {banner.ok ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
          {banner.msg}
        </div>
      )}

      {/* ── NIQS circular notice ───────────────────────────────────────────── */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 space-y-2">
        <div className="flex items-center gap-2 font-bold text-amber-800">
          <Building2 className="w-5 h-5" />
          NIQS Circular — Tertiary Institutions Data Collection
        </div>
        <p className="text-sm text-amber-700 leading-relaxed">
          The Nigerian Institute of Quantity Surveyors (NIQS) is compiling a comprehensive database of all
          tertiary institutions in Nigeria offering Quantity Surveying programmes (B.Sc and HND).
          As chapter chairman or secretary, please submit details of institutions within your chapter's
          jurisdiction using the form below. Each submission should include the institution name, programme
          type, accreditation status, and the Head of Department's contact information to assist NIQS
          in graduate data collection and accreditation follow-up.
        </p>
      </div>

      {/* ── Forbidden state ────────────────────────────────────────────────── */}
      {isForbidden && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
          <p className="font-semibold text-red-700">Access Restricted</p>
          <p className="text-sm text-red-600 mt-1">
            Only the chapter chairman or secretary can submit institution data.
          </p>
        </div>
      )}

      {/* ── Submit form panel ──────────────────────────────────────────────── */}
      {!isForbidden && (
        <div className="bg-white border rounded-xl shadow-sm overflow-hidden">
          <button
            onClick={() => setShowForm((v) => !v)}
            className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors text-left"
          >
            <span className="font-semibold text-gray-900 flex items-center gap-2">
              <Plus className="w-4 h-4 text-indigo-600" />
              Submit an Institution
            </span>
            {showForm
              ? <ChevronUp className="w-4 h-4 text-gray-400" />
              : <ChevronDown className="w-4 h-4 text-gray-400" />}
          </button>

          {showForm && (
            <div className="border-t px-6 py-5">
              <SubmitForm onSuccess={() => {
                flash(true, "Institution submitted successfully.");
                setShowForm(false);
              }} />
            </div>
          )}
        </div>
      )}

      {/* ── Submissions list ───────────────────────────────────────────────── */}
      <div className="bg-white border rounded-xl shadow-sm">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="font-semibold text-gray-900">
            Your Chapter's Submissions
            {entries.length > 0 && (
              <span className="ml-2 px-2 py-0.5 rounded-full text-xs font-bold bg-indigo-100 text-indigo-700">
                {entries.length}
              </span>
            )}
          </h2>
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm hover:bg-gray-50 disabled:opacity-40"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isFetching ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>

        {isLoading ? (
          <div className="py-12 text-center text-gray-400 text-sm">Loading…</div>
        ) : entries.length === 0 ? (
          <div className="py-12 text-center text-gray-400">
            <Building2 className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm">No submissions yet. Use the form above to add one.</p>
          </div>
        ) : (
          <div className="divide-y">
            {entries.map((entry) => (
              <div key={entry.id} className="px-6 py-4 flex items-start justify-between gap-4">
                <div className="space-y-1 min-w-0">
                  <div className="font-semibold text-gray-900 truncate">{entry.institution_name}</div>
                  <div className="flex flex-wrap items-center gap-2">
                    <ProgBadge type={entry.programme_type} other={entry.programme_other} />
                    <AccredBadge status={entry.accreditation_status} />
                  </div>
                  <div className="text-xs text-gray-500">
                    HOD: <span className="font-medium text-gray-700">{entry.hod_name}</span>
                    {entry.hod_email && <> · {entry.hod_email}</>}
                    {entry.hod_phone && <> · {entry.hod_phone}</>}
                  </div>
                  {entry.contact_person_name && (
                    <div className="text-xs text-gray-500">
                      Contact: <span className="font-medium text-gray-700">{entry.contact_person_name}</span>
                      {entry.contact_person_email && <> · {entry.contact_person_email}</>}
                    </div>
                  )}
                  <div className="text-[10px] text-gray-400">
                    Submitted by {entry.submitted_by ?? "you"} · {fmtDate(entry.created_at)}
                  </div>
                </div>
                <button
                  onClick={() => setDeleteTarget(entry)}
                  disabled={deleting}
                  className="flex-shrink-0 p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-40"
                  title="Delete entry"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Delete modal ───────────────────────────────────────────────────── */}
      {deleteTarget && (
        <DeleteModal
          entry={deleteTarget}
          onConfirm={handleDelete}
          onClose={() => setDeleteTarget(null)}
          loading={deleting}
        />
      )}
    </div>
  );
}
